import { Suspense } from 'react';

import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { LoanOverviewRBL } from './LoanOverviewRBL';
import { useGetLoanOverviewDetails } from './useGetLoanOverviewDetails';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { Rate } from '../../../ui/rate';

export const LoanOverview = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div data-testId="loan-overview-loading">
                        <MainContentPending />
                    </div>
                }
            >
                <LoanOverviewContent accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const LoanOverviewContent = ({ accountId }: { accountId: string }) => {
    const {
        interest,
        outstandingBalance,
        principal,
        interestRate,
        estimatedTimeToRepay,
        sweepFraction
    } = useGetLoanOverviewDetails(accountId);

    if (estimatedTimeToRepay && sweepFraction) {
        return (
            <LoanOverviewRBL
                outstandingBalance={outstandingBalance}
                estimatedTimeToRepay={estimatedTimeToRepay}
                sweepFraction={sweepFraction}
            />
        );
    }

    return (
        <div
            data-testid="loan-overview"
            className="col-span-5 max-sm:col-span-full max-sm:mr-xl max-sm:pb-0"
        >
            <div className="flex flex-row gap-xl max-sm:flex-col">
                {/* Outstanding Balance */}
                <div
                    className="mr-l flex flex-col justify-between border-secondary-80 pr-l max-sm:col-span-full min-l:border-r"
                    aria-label="outstanding-balance"
                >
                    <span>
                        <Text className="text-l font-med">
                            <FormattedMessage
                                id="_.dashboard.outstanding_balance"
                                defaultMessage="Outstanding Balance"
                            />
                        </Text>
                        <Text className="text-xs font-reg">
                            <FormattedMessage
                                id="_.dashboard.outstanding_balance_subtext"
                                defaultMessage="Amount left to repay inc. accrued interest."
                            />
                        </Text>
                    </span>
                    <Text className="mt-xs text-2xl font-med">
                        <FormattedCurrency value={outstandingBalance!} />
                    </Text>
                </div>
                <div className="flex flex-row max-sm:flex-row-reverse min-l:gap-m min-xl:gap-5xl">
                    {/* Interest Charges */}
                    <div
                        className="flex flex-1 flex-col justify-between"
                        aria-label="interest"
                    >
                        <span>
                            <Text className="text-l font-med">
                                <FormattedMessage
                                    id="_.dashboard.interestAccrued"
                                    defaultMessage="Interest"
                                />
                            </Text>
                            <Text className="text-xs font-reg">
                                <FormattedMessage
                                    id="_.dashboard.interest_text"
                                    defaultMessage="({approvalInterestRate} per month)"
                                    values={{
                                        approvalInterestRate: (
                                            <Rate value={interestRate!} />
                                        )
                                    }}
                                />
                            </Text>
                        </span>
                        <Text className="mt-xs text-2xl font-med">
                            <FormattedCurrency value={interest!} />
                        </Text>
                    </div>

                    {/* Funding Amount */}
                    <div
                        className="flex flex-1 flex-col justify-between"
                        aria-label="current-principal"
                    >
                        <Text className="whitespace-nowrap text-l font-med">
                            <FormattedMessage
                                id="_.dashboard.current_principal"
                                defaultMessage="Current principal"
                            />
                        </Text>

                        <Text className="mt-xs text-2xl font-med">
                            <FormattedCurrency value={principal!} />
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};
